import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
    X, Save, AlertCircle, Loader2, Dumbbell, 
    Plus, Search, Filter, Timer, Trash2, Eye,
    ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
    Grid, List, Target, Zap, Clock, Weight, GripVertical,
    Maximize2, Minimize2
} from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import NewExerciseSelector from '../common/NewExerciseSelector';
import MuscleBadges from '../common/MuscleBadges';
import ImprovedUserExerciseForm from '../Standalone/ImprovedUserExerciseForm';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import usePaginatedExercises from '../../hooks/usePaginatedExercises';
import RecoveryMinSecInputs from './common/RecoveryMinSecInputs';
// Componente locale per esercizio ordinabile
const SortableExerciseItem = ({ exercise, previousExercise, onUpdate, onRemove, onToggleDetails, isExpanded, isFirst }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: exercise.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`rounded-lg border border-[#dad8e5] bg-white p-4 dark:border-white/10 dark:bg-gray-900 ${
                isDragging ? 'shadow-lg' : 'shadow-sm'
            } transition-all duration-200`}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <div
                        {...attributes}
                        {...listeners}
                        className="cursor-grab p-1 text-gray-400 hover:cursor-grabbing hover:text-[#372d83] dark:hover:text-white/70"
                    >
                        <GripVertical className="h-4 w-4" />
                    </div>
                    
                    {/* Immagine esercizio */}
                    <div className="w-12 h-12 flex-shrink-0">
                        {exercise.immagine_nome ? (
                            <img
                                src={`/api/serve_image.php?filename=${encodeURIComponent(exercise.immagine_nome)}`}
                                alt={exercise.nome}
                                className="h-full w-full select-none rounded-lg bg-[#eeebf5] object-cover dark:bg-gray-800"
                                onContextMenu={(e) => e.preventDefault()}
                                draggable={false}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <div 
                            className="hidden h-full w-full flex items-center justify-center rounded-lg bg-[#eeebf5] dark:bg-gray-800"
                        >
                            <Dumbbell className="h-6 w-6 text-[#bca8d1] dark:text-white/45" />
                        </div>
                    </div>
                    
                    <div className="flex-1">
                        <h3 className="font-medium text-[#3a3a3a] dark:text-white">{exercise.nome}</h3>
                        {exercise.descrizione && (
                            <p className="mt-1 text-sm text-[#3a3a3a]/80 dark:text-white/70">{exercise.descrizione}</p>
                        )}
                        
                        {/* Badge muscoli */}
                        <div className="mt-2">
                            <MuscleBadges
                                primaryMuscle={exercise.primary_muscle}
                                secondaryMuscles={exercise.secondary_muscles}
                                variant="compact"
                                showCategory={false}
                            />
                        </div>
                        
                        {/* Badge tipo di serie */}
                        <div className="mt-2 flex items-center space-x-2">
                            {exercise.set_type && exercise.set_type !== 'normal' && (
                                <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                    exercise.set_type === 'superset' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' :
                                    exercise.set_type === 'dropset' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' :
                                    exercise.set_type === 'circuit' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' :
                                    exercise.set_type === 'giant_set' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' :
                                    'bg-[#eeebf5] text-[#3a3a3a] dark:bg-gray-800 dark:text-white/90'
                                }`}>
                                    {exercise.set_type === 'superset' ? 'Superset' :
                                     exercise.set_type === 'dropset' ? 'Dropset' :
                                     exercise.set_type === 'circuit' ? 'Circuit' :
                                     exercise.set_type === 'giant_set' ? 'Giant Set' :
                                     exercise.set_type}
                                </span>
                            )}
                            {exercise.linked_to_previous && (
                                <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 font-medium">
                                    🔗 Collegato
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center space-x-2">
                    <button
                        type="button"
                        onClick={() => onToggleDetails(exercise.id)}
                        className="p-2 text-gray-400 transition-colors hover:text-[#372d83] dark:hover:text-white/75"
                    >
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    
                    <button
                        type="button"
                        onClick={() => onRemove(exercise.id)}
                        className="p-2 text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Dettagli espandibili */}
            {isExpanded && (
                <div className="mt-4 border-t border-[#dad8e5] pt-4 dark:border-white/10">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                                Serie
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={exercise.serie ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    onUpdate(exercise.id, 'serie', value === '' ? '' : parseInt(value) || '');
                                }}
                                onBlur={(e) => {
                                    if (e.target.value === '' || e.target.value === '0') {
                                        onUpdate(exercise.id, 'serie', 3);
                                    }
                                }}
                                onFocus={(e) => e.target.select()}
                                className="w-full rounded-md border border-[#dad8e5] bg-white px-3 py-2 text-[#3a3a3a] focus:border-[#372d83] focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30"
                            />
                        </div>
                        
                        <div>
                            <label className="mb-1 block text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                                {(() => {
                                    const isCardio = exercise.is_cardio === 1;
                                    const isIsometricScheda = exercise.is_isometric_scheda === 1;
                                    if (isCardio) return 'Minuti';
                                    if (isIsometricScheda) return 'Secondi';
                                    return 'Ripetizioni';
                                })()}
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={exercise.ripetizioni ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    onUpdate(exercise.id, 'ripetizioni', value === '' ? '' : parseInt(value) || '');
                                }}
                                onBlur={(e) => {
                                    if (e.target.value === '' || e.target.value === '0') {
                                        onUpdate(exercise.id, 'ripetizioni', 12);
                                    }
                                }}
                                onFocus={(e) => e.target.select()}
                                className="w-full rounded-md border border-[#dad8e5] bg-white px-3 py-2 text-[#3a3a3a] focus:border-[#372d83] focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30"
                                placeholder={(() => {
                                    const isCardio = exercise.is_cardio === 1;
                                    const isIsometricScheda = exercise.is_isometric_scheda === 1;
                                    if (isCardio) return 'Minuti (es. 30)';
                                    if (isIsometricScheda) return 'Secondi di tenuta';
                                    return 'Numero ripetizioni';
                                })()}
                            />
                        </div>
                        
                        {/* Peso - nascosto per esercizi cardio */}
                        {exercise.is_cardio !== 1 && (
                        <div>
                            <label className="mb-1 block text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                                Peso (kg)
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={exercise.peso ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    onUpdate(exercise.id, 'peso', value === '' ? '' : parseFloat(value) || '');
                                }}
                                onBlur={(e) => {
                                    if (e.target.value === '') {
                                        onUpdate(exercise.id, 'peso', 0);
                                    }
                                }}
                                onFocus={(e) => e.target.select()}
                                className="w-full rounded-md border border-[#dad8e5] bg-white px-3 py-2 text-[#3a3a3a] focus:border-[#372d83] focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30"
                            />
                        </div>
                        )}
                        
                        <RecoveryMinSecInputs
                            valueSeconds={exercise.tempo_recupero}
                            onSecondsChange={(sec) => onUpdate(exercise.id, 'tempo_recupero', sec)}
                            defaultTotalSeconds={90}
                            focusColor="emerald"
                            gymDarkInputs
                        />
                    </div>
                    
                    {/* Tipo di serie */}
                    <div className="mt-4">
                        <label className="mb-1 block text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                            Tipo di Serie
                        </label>
                        <select
                            value={exercise.set_type || 'normal'}
                            onChange={(e) => onUpdate(exercise.id, 'set_type', e.target.value)}
                            className="w-full rounded-md border border-[#dad8e5] bg-white px-3 py-2 text-[#3a3a3a] focus:border-[#372d83] focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30"
                        >
                            <option value="normal">Normale</option>
                            <option value="superset">Superset</option>
                            <option value="dropset">Dropset</option>
                            <option value="circuit">Circuit</option>
                            <option value="giant_set">Giant Set</option>
                        </select>
                    </div>
                    
                    {/* Collegamento all'esercizio precedente */}
                    {!isFirst && exercise.set_type && exercise.set_type !== 'normal' && previousExercise && exercise.set_type === previousExercise.set_type && (
                        <div className="mt-4">
                            <div className="flex items-center space-x-3">
                                <input
                                    type="checkbox"
                                    id={`linked-${exercise.id}`}
                                    checked={exercise.linked_to_previous || false}
                                    onChange={(e) => onUpdate(exercise.id, 'linked_to_previous', e.target.checked)}
                                    className="h-4 w-4 rounded border-[#dad8e5] text-[#372d83] focus:ring-[#372d83]/40 dark:border-gray-700 dark:text-gray-300"
                                />
                                <label htmlFor={`linked-${exercise.id}`} className="text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                                    Collegato all'esercizio precedente
                                </label>
                            </div>
                            <p className="mt-1 text-xs text-gray-500 dark:text-white/60">
                                Collega questo esercizio al precedente per formare superset, dropset, ecc.
                            </p>
                        </div>
                    )}
                    
                    {/* Modalità Isometrica (solo se esercizio non è cardio) */}
                    {exercise.is_cardio !== 1 && (
                        <div className="mt-4">
                            <div className="flex items-center space-x-3">
                                <input
                                    type="checkbox"
                                    id={`is_isometric_scheda-${exercise.id}`}
                                    checked={exercise.is_isometric_scheda === 1}
                                    onChange={(e) => onUpdate(exercise.id, 'is_isometric_scheda', e.target.checked ? 1 : 0)}
                                    className="h-4 w-4 rounded border-[#dad8e5] text-[#372d83] focus:ring-[#372d83]/40 dark:border-gray-700 dark:text-gray-300"
                                />
                                <label htmlFor={`is_isometric_scheda-${exercise.id}`} className="text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                                    Modalità Isometrica (usa secondi invece di ripetizioni)
                                </label>
                            </div>
                            <p className="ml-7 mt-1 text-xs text-gray-500 dark:text-white/60">
                                Se attivo, questo esercizio verrà eseguito a tempo (secondi) invece che a ripetizioni
                            </p>
                        </div>
                    )}
                    
                    {/* Campi Cardio (solo se esercizio è cardio) */}
                    {exercise.is_cardio === 1 && (
                        <div className="mt-4 grid grid-cols-2 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                                    Calorie (opzionale)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={exercise.calorie || ''}
                                    onChange={(e) => onUpdate(exercise.id, 'calorie', e.target.value ? parseInt(e.target.value) : null)}
                                    className="w-full rounded-md border border-[#dad8e5] bg-white px-3 py-2 text-[#3a3a3a] focus:border-[#372d83] focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30"
                                    placeholder="Calorie bruciate"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                                    BPM (opzionale)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={exercise.bpm || ''}
                                    onChange={(e) => onUpdate(exercise.id, 'bpm', e.target.value ? parseInt(e.target.value) : null)}
                                    className="w-full rounded-md border border-[#dad8e5] bg-white px-3 py-2 text-[#3a3a3a] focus:border-[#372d83] focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30"
                                    placeholder="Battiti per minuto"
                                />
                            </div>
                        </div>
                    )}
                    
                    {/* Note Trainer - Sistema Note Duali */}
                    <div className="mt-4">
                        <label className="mb-1 block text-sm font-medium text-[#3a3a3a] dark:text-white/85">
                            📝 Note Trainer
                        </label>
                        <textarea
                            value={exercise.notes?.trainer ?? ''}
                            onChange={(e) => {
                                // 🔥 DB-001: Mantieni le note esistenti (user, system) quando si modifica trainer
                                const existingNotes = exercise.notes || { trainer: null, user: null, system: null };
                                const newNotes = {
                                    trainer: e.target.value || null,
                                    user: existingNotes.user || null,
                                    system: existingNotes.system || null
                                };
                                onUpdate(exercise.id, 'notes', newNotes);
                            }}
                            rows={3}
                            maxLength={500}
                            className="w-full rounded-md border border-[#dad8e5] bg-white px-3 py-2 text-[#3a3a3a] focus:border-[#372d83] focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30"
                            placeholder="Aggiungi istruzioni per l'utente (es: 'Mantieni la schiena dritta', 'Controlla la discesa')"
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-white/60">
                            💡 Queste note saranno visibili all'utente durante l'allenamento
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default function EditWorkoutModal({ workout, client, isOpen, onClose, onSuccess }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    
    // State principale
    const [formData, setFormData] = useState({
        nome: '',
        descrizione: ''
    });
    
    // State esercizi
    const [selectedExercises, setSelectedExercises] = useState([]);
    const [expandedExercise, setExpandedExercise] = useState(null);
    
    // State per filtri avanzati (come in archivio esercizi)
    const [filters, setFilters] = useState({
        equipment: '',
        primaryMuscle: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    
    // WEBAPP-021: Usa hook paginato invece di caricamento completo
    const {
        exercises: availableExercises,
        loading: loadingExercises,
        initialLoading,
        hasMore,
        loadMore,
        search: searchExercises,
        refresh: refreshExercises
    } = usePaginatedExercises({
        initialLimit: 50,
        enableInfiniteScroll: true
    });
    
    // Gestisce il cambio filtri da NewExerciseSelector
    const handleFiltersChange = useCallback((newFilters) => {
        const newEquipment = newFilters.equipment || '';
        const newPrimaryMuscle = newFilters.primaryMuscle || '';
        
        setFilters({
            equipment: newEquipment,
            primaryMuscle: newPrimaryMuscle
        });
        
        // Applica i filtri tramite searchExercises solo se c'è almeno un filtro attivo o ricerca
        const hasFilters = newEquipment || newPrimaryMuscle;
        const hasSearch = searchTerm && searchTerm.trim().length >= 2;
        
        if (hasFilters || hasSearch) {
            searchExercises(hasSearch ? searchTerm : '', {
                equipment: newEquipment && newEquipment.trim() !== '' ? newEquipment.trim() : undefined,
                primaryMuscle: newPrimaryMuscle && newPrimaryMuscle.trim() !== '' ? newPrimaryMuscle.trim() : undefined
            });
        } else {
            // WEBAPP-GYM-011: reset filtri senza testo ricerca → ricarica elenco completo
            searchExercises('', {});
        }
    }, [searchExercises, searchTerm]);
    
    // Gestisce la ricerca testuale
    const handleSearchChange = useCallback((term) => {
        setSearchTerm(term);
        
        // Applica ricerca con filtri attivi solo se c'è almeno ricerca (>=2 caratteri) o filtri
        const hasSearch = term && term.trim().length >= 2;
        const hasFilters = filters.equipment || filters.primaryMuscle;
        
        if (hasSearch || hasFilters) {
            searchExercises(hasSearch ? term.trim() : '', {
                equipment: filters.equipment && filters.equipment.trim() !== '' ? filters.equipment.trim() : undefined,
                primaryMuscle: filters.primaryMuscle && filters.primaryMuscle.trim() !== '' ? filters.primaryMuscle.trim() : undefined
            });
        }
    }, [searchExercises, filters]);
    
    // State UI
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [showAddExerciseForm, setShowAddExerciseForm] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [activeTab, setActiveTab] = useState('selection'); // 'selection' | 'selected'
    const [isFullscreen, setIsFullscreen] = useState(false); // WEBAPP-GYM-004: Stato fullscreen
    
    // Drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (isOpen && workout) {
            // Carica i dati della scheda esistente
            setFormData({
                nome: workout.nome || '',
                descrizione: workout.descrizione || ''
            });
            
            // Carica gli esercizi selezionati
            if (workout.esercizi && Array.isArray(workout.esercizi)) {
                // Mappa gli esercizi: NON sovrascrivere `id` con esercizio_id catalogo.
                // L'API GET restituisce `id` = PK di scheda_esercizi; serve nel PUT per non
                // far trattare tutte le righe come INSERT (che cancellerebbe tutto lo storico serie).
                const mappedExercises = workout.esercizi.map(ex => {
                    const catalogEsercizioId = ex.esercizio_id ?? ex.exercise_id;
                    
                    // Parse note JSON se presenti
                    let notes = { trainer: null, user: null, system: null };
                    if (ex.note && typeof ex.note === 'string' && ex.note.trim().startsWith('{')) {
                        try {
                            notes = JSON.parse(ex.note);
                        } catch (e) {
                            // Se non è JSON valido, considera come note trainer legacy
                            notes = { trainer: ex.note, user: null, system: null };
                        }
                    } else if (ex.note) {
                        // Note legacy (stringa semplice) → considera come note trainer
                        notes = { trainer: ex.note, user: null, system: null };
                    }
                    
                    // Per esercizi cardio: converti secondi in minuti per l'UI
                    let ripetizioni = ex.ripetizioni || 12;
                    if (ex.is_cardio === 1 && ripetizioni > 0) {
                        // Se è cardio, ripetizioni contiene secondi nel DB, converti in minuti per l'UI
                        ripetizioni = Math.round(ripetizioni / 60);
                    }
                    
                    return {
                        ...ex,
                        // id: rimane da ...ex = scheda_esercizi.id (chiave riga / DnD)
                        esercizio_id: catalogEsercizioId,
                        notes: notes, // Aggiungi notes parsate
                        linked_to_previous: Boolean(ex.linked_to_previous), // Converti a boolean
                        ripetizioni: ripetizioni, // Convertito per cardio
                        is_isometric_scheda: ex.is_isometric_scheda || 0, // Da scheda_esercizi
                        calorie: ex.calorie || null, // Da scheda_esercizi
                        bpm: ex.bpm || null // Da scheda_esercizi
                    };
                });
                setSelectedExercises(mappedExercises);
            } else {
                setSelectedExercises([]);
            }
            
            setExpandedExercise(null);
            setError(null);
            setMessage('');
            setValidationErrors({});
            setActiveTab('selected'); // Start with selected exercises for editing
            
            // WEBAPP-021: L'hook usePaginatedExercises gestisce già il caricamento iniziale
        }
    }, [isOpen, workout]);

    const handleSelectExercise = (exercise) => {
        const catalogId = exercise.esercizio_id || exercise.id;
        const exists = selectedExercises.find(e => e.esercizio_id === catalogId);
        if (exists) {
            setSelectedExercises(prev => prev.filter(e => e.esercizio_id !== catalogId));
            if (expandedExercise === exists.id) {
                setExpandedExercise(null);
            }
        } else {
            const esercizioId = catalogId;
            const rowId = typeof crypto !== 'undefined' && crypto.randomUUID
                ? `new-${crypto.randomUUID()}`
                : `new-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
            
            setSelectedExercises(prev => [
                ...prev, 
                { 
                    ...exercise,
                    id: rowId, // id riga elenco (nuova riga, non è scheda_esercizi.id)
                    esercizio_id: esercizioId, // ID catalogo esercizi
                    serie: exercise.serie_default || (exercise.is_cardio === 1 ? 1 : 3), // Cardio: sempre 1 serie
                    ripetizioni: exercise.ripetizioni_default || 12, 
                    peso: exercise.peso_default || 0,
                    tempo_recupero: exercise.tempo_recupero || 90,
                    notes: { trainer: null, user: null, system: null },
                    set_type: 'normal',
                    linked_to_previous: false,
                    is_isometric_scheda: 0, // Default: non isometrico a livello scheda
                    calorie: null, // Default: nessuna caloria
                    bpm: null // Default: nessun BPM
                }
            ]);
            // NON cambiare tab - rimani nella tab corrente (Selezione Esercizi)
        }
    };

    const updateExerciseDetail = (id, field, value) => {
        setSelectedExercises(prev =>
            prev.map(e => {
                if (e.id !== id) return e;
                
                // Validazione: se esercizio è cardio, non può essere isometrico nella stessa scheda
                if (field === 'is_isometric_scheda' && value === 1 && e.is_cardio === 1) {
                    setError('Un esercizio cardio non può essere isometrico nella stessa scheda');
                    return e; // Non modificare
                }
                
                // Se set_type è 'normal', linked_to_previous deve essere false
                if (field === 'set_type' && value === 'normal') {
                    return { ...e, [field]: value, linked_to_previous: false };
                }
                
                // Se linked_to_previous è true, il primo esercizio non può essere collegato
                if (field === 'linked_to_previous' && value === true) {
                    const exerciseIndex = prev.findIndex(ex => ex.id === id);
                    if (exerciseIndex === 0) {
                        return e; // Non modificare il primo esercizio
                    }
                }
                
                return { ...e, [field]: value };
            })
        );
    };

    const removeExercise = (id) => {
        setSelectedExercises(prev => prev.filter(e => e.id !== id));
        if (expandedExercise === id) {
            setExpandedExercise(null);
        }
    };

    const handleToggleExpanded = (exerciseId) => {
        setExpandedExercise(prev => prev === exerciseId ? null : exerciseId);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            setSelectedExercises(prev => {
                const oldIndex = prev.findIndex(item => item.id === active.id);
                const newIndex = prev.findIndex(item => item.id === over.id);
                return arrayMove(prev, oldIndex, newIndex);
            });
        }
    };

    const handleExerciseCreated = async (result) => {
        // result contiene {success: true, message: '...', exercise_id: 293}
        if (result.success && result.exercise_id) {
            try {
                // Recupera l'esercizio completo dal backend usando l'ID specifico
                const api = getAuthenticatedApi();
                const response = await api.get(`/esercizi.php?id=${result.exercise_id}`);
                
                if (response.data && response.data.id) {
                    const newExercise = response.data;
                    
                    // WEBAPP-021: Ricarica lista esercizi per includere il nuovo
                    refreshExercises();
                    
                    // Seleziona automaticamente l'esercizio appena creato
                    handleSelectExercise(newExercise);
                    
                    setMessage('Esercizio creato e aggiunto con successo!');
                    setMessageType('success');
                } else {
                    console.error('Esercizio creato ma non trovato:', result.exercise_id);
                    setMessage('Esercizio creato ma errore nel caricamento');
                    setMessageType('warning');
                }
            } catch (err) {
                console.error('Errore nel caricamento dell\'esercizio creato:', err);
                setMessage('Esercizio creato ma errore nel caricamento');
                setMessageType('warning');
            }
            
            setTimeout(() => {
                setMessage('');
                setMessageType('');
            }, 3000);
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.nome.trim()) {
            errors.nome = 'Il nome della scheda è obbligatorio';
        }
        
        if (selectedExercises.length === 0) {
            errors.exercises = 'Aggiungi almeno un esercizio';
        }
        
        // WEBAPP-GYM-003: Validazione dati numerici degli esercizi
        selectedExercises.forEach((ex, index) => {
            const exerciseErrors = [];
            
            const serie = parseInt(ex.serie);
            if (!serie || serie < 1) {
                exerciseErrors.push(`Serie non valide`);
            }
            
            const reps = parseInt(ex.ripetizioni);
            if (!reps || reps < 0) {
                const label = ex.is_cardio === 1 ? 'Minuti' : (ex.is_isometric_scheda === 1 ? 'Secondi' : 'Ripetizioni');
                exerciseErrors.push(`${label} non validi`);
            }
            
            const peso = parseFloat(ex.peso);
            if (isNaN(peso) || peso < 0) {
                exerciseErrors.push(`Peso non valido`);
            }
            
            const recupero = parseInt(ex.tempo_recupero);
            if (isNaN(recupero) || recupero < 0) {
                exerciseErrors.push(`Tempo recupero non valido`);
            }
            
            if (exerciseErrors.length > 0) {
                errors[`exercise_${index}`] = `Esercizio "${ex.nome}": ${exerciseErrors.join(', ')}`;
            }
        });
        
        setValidationErrors(errors);
        
        if (Object.keys(errors).length > 0) {
            const errorMessages = Object.values(errors).join('\n');
            setError(errorMessages);
            return false;
        }
        
        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);
        setError(null);

        try {
            const api = getAuthenticatedApi();
            const schedaData = {
                nome: formData.nome.trim(),
                descrizione: formData.descrizione.trim(),
                user_id: client.id,
                esercizi: selectedExercises.map(ex => {
                    const esercizioId = ex.esercizio_id ?? ex.exercise_id;
                    
                    if (!esercizioId) {
                        console.error('⚠️ Esercizio senza ID catalogo valido:', ex);
                    }
                    
                    // WEBAPP-GYM-003: Sanitizza e valida i dati numerici
                    const serie = parseInt(ex.serie) || (ex.is_cardio === 1 ? 1 : 3);
                    let ripetizioni = parseInt(ex.ripetizioni) || 12;
                    const peso = parseFloat(ex.peso) || 0;
                    const tempoRecupero = parseInt(ex.tempo_recupero) || 90;
                    
                    // Per esercizi cardio: converti minuti in secondi
                    if (ex.is_cardio === 1) {
                        // Se è cardio, ripetizioni contiene minuti nell'UI, converti in secondi per il DB
                        ripetizioni = ripetizioni * 60;
                    }
                    
                    const row = {
                        esercizio_id: esercizioId,
                        serie: Math.max(1, serie),
                        ripetizioni: Math.max(0, ripetizioni),
                        peso: Math.max(0, peso),
                        tempo_recupero: Math.max(0, tempoRecupero),
                        note: ex.notes ? JSON.stringify(ex.notes) : JSON.stringify({ trainer: null, user: null, system: null }),
                        set_type: ex.set_type,
                        linked_to_previous: ex.linked_to_previous,
                        is_isometric_scheda: ex.is_isometric_scheda || 0,
                        calorie: ex.calorie || null,
                        bpm: ex.bpm || null
                    };
                    // PK scheda_esercizi: righe già salvate; assente per righe nuove (string id temp)
                    if (typeof ex.id === 'number' && Number.isFinite(ex.id) && ex.id > 0) {
                        row.id = ex.id;
                    }
                    return row;
                })
            };

            await api.put(`/schede.php?id=${workout.id}`, schedaData);
            onSuccess && onSuccess();
            onClose();

        } catch (err) {
            console.error('Errore nella modifica scheda:', err);
            setError(err.response?.data?.error || 'Errore nel salvataggio della scheda');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !workout) return null;

    return (
        <div className={`fixed inset-0 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm ${isFullscreen ? 'z-[9999]' : 'z-50'}`}>
            <div className={`flex flex-col overflow-hidden rounded-2xl border border-[#dad8e5] bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35 ${
                isFullscreen ? 'h-full max-h-full w-full max-w-full' : 'h-[90vh] max-h-[90vh] w-[90vw] max-w-[90vw]'
            }`}>
                {/* Header Fisso */}
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white dark:bg-gradient-to-r dark:from-gray-950 dark:via-gray-900 dark:to-gray-900">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <h2 className="text-2xl font-bold mb-2">
                                Modifica Scheda
                            </h2>
                            <p className="text-emerald-100">
                                Cliente: <span className="font-semibold">{client?.nome || client?.username || 'N/A'}</span>
                            </p>
                        </div>
                        
                        {/* WEBAPP-GYM-004: Bottoni fullscreen e chiudi */}
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setIsFullscreen(!isFullscreen)}
                                className="text-white hover:bg-white/20 rounded-lg transition-colors p-2"
                                title={isFullscreen ? 'Esci da schermo intero' : 'Modalità schermo intero'}
                            >
                                {isFullscreen ? (
                                    <Minimize2 size={20} />
                                ) : (
                                    <Maximize2 size={20} />
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="text-white hover:text-gray-200 transition-colors p-2"
                            >
                                <X size={24} />
                            </button>
                        </div>
                    </div>
                    
                    {/* Form Info Scheda */}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="mb-2 block text-sm font-medium text-emerald-100 dark:text-white/90">
                                Nome Scheda <span className="text-red-300">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.nome}
                                onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                                className={`w-full rounded-lg border-2 bg-white/20 px-4 py-3 text-white placeholder-gray-200 transition-colors focus:border-white focus:ring-2 focus:ring-white dark:bg-white/15 dark:placeholder-white/50 ${
                                    validationErrors.nome ? 'border-red-300' : 'border-emerald-300 dark:border-white/25'
                                }`}
                                placeholder="Es. Scheda Push Professionale"
                            />
                            {validationErrors.nome && (
                                <p className="mt-1 text-sm text-red-200 flex items-center">
                                    <AlertCircle className="h-4 w-4 mr-1" />
                                    {validationErrors.nome}
                                </p>
                            )}
                        </div>
                        
                        <div>
                            <label className="mb-2 block text-sm font-medium text-emerald-100 dark:text-white/90">
                                Descrizione
                            </label>
                            <input
                                type="text"
                                value={formData.descrizione}
                                onChange={(e) => setFormData(prev => ({ ...prev, descrizione: e.target.value }))}
                                className="w-full rounded-lg border-2 border-emerald-300 bg-white/20 px-4 py-3 text-white placeholder-gray-200 transition-colors focus:border-white focus:ring-2 focus:ring-white dark:border-white/25 dark:bg-white/15 dark:placeholder-white/50"
                                placeholder="Descrizione della scheda..."
                            />
                        </div>
                    </div>
                </div>

                {/* Messaggi */}
                {(message || error) && (
                    <div className="border-b border-[#dad8e5] bg-[#eeebf5] px-6 py-3 dark:border-white/10 dark:bg-gray-900">
                        {message && (
                            <div className={`p-3 rounded-md ${
                                messageType === 'success' ? 'bg-green-100 text-green-800' : 
                                messageType === 'error' ? 'bg-red-100 text-red-800' : 
                                'bg-blue-100 text-blue-800'
                            }`}>
                                {message}
                            </div>
                        )}
                        {error && (
                            <div className="p-3 bg-red-100 text-red-800 rounded-md">
                                {error}
                            </div>
                        )}
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="border-b border-[#dad8e5] bg-[#eeebf5] dark:border-white/10 dark:bg-gray-900">
                    <div className="flex">
                        <button
                            type="button"
                            onClick={() => setActiveTab('selection')}
                            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                                activeTab === 'selection'
                                    ? 'border-b-2 border-emerald-500 bg-emerald-50 text-emerald-800 dark:border-indigo-500 dark:bg-gray-950 dark:text-gray-300'
                                    : 'text-[#3a3a3a]/75 hover:text-[#372d83] dark:text-white/70 dark:hover:text-white'
                            }`}
                        >
                            <div className="flex items-center justify-center space-x-2">
                                <Search className="h-4 w-4" />
                                <span>Selezione Esercizi</span>
                                <span className="rounded-full border border-[#dad8e5] bg-white px-2 py-1 text-xs text-[#3a3a3a] dark:border-transparent dark:bg-gray-800 dark:text-white">
                                    {availableExercises.length}
                                </span>
                            </div>
                        </button>
                        
                        <button
                            type="button"
                            onClick={() => setActiveTab('selected')}
                            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                                activeTab === 'selected'
                                    ? 'border-b-2 border-emerald-500 bg-emerald-50 text-emerald-800 dark:border-indigo-500 dark:bg-gray-950 dark:text-gray-300'
                                    : 'text-[#3a3a3a]/75 hover:text-[#372d83] dark:text-white/70 dark:hover:text-white'
                            }`}
                        >
                            <div className="flex items-center justify-center space-x-2">
                                <Target className="h-4 w-4" />
                                <span>Esercizi Selezionati</span>
                                <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs text-emerald-700 dark:bg-gray-800 dark:text-gray-300">
                                    {selectedExercises.length}
                                </span>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-hidden min-h-0">
                    {activeTab === 'selection' ? (
                        <div className="h-full min-h-0 overflow-hidden">
                            <NewExerciseSelector
                                searchTerm={searchTerm}
                                exercises={availableExercises}
                                selectedExercises={selectedExercises}
                                onSelectExercise={handleSelectExercise}
                                onShowAddExerciseForm={() => setShowAddExerciseForm(true)}
                                loading={loadingExercises || initialLoading}
                                hasMore={hasMore}
                                onLoadMore={loadMore}
                                onFiltersChange={handleFiltersChange}
                                onSearchChange={handleSearchChange}
                                gymExerciseSelector
                            />
                        </div>
                    ) : (
                        <div className="h-full min-h-0 overflow-y-auto bg-[#faf9fc] p-6 dark:bg-gray-900">
                            {validationErrors.exercises && (
                                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                        <AlertCircle className="h-4 w-4 mr-2" />
                                        {validationErrors.exercises}
                                    </p>
                                </div>
                            )}

                            {selectedExercises.length > 0 ? (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={selectedExercises.map(e => e.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <div className="space-y-4">
                                            {selectedExercises.map((exercise, index) => (
                                                <SortableExerciseItem
                                                    key={exercise.id}
                                                    exercise={exercise}
                                                    previousExercise={index > 0 ? selectedExercises[index - 1] : null}
                                                    onUpdate={updateExerciseDetail}
                                                    onRemove={removeExercise}
                                                    onToggleDetails={handleToggleExpanded}
                                                    isExpanded={expandedExercise === exercise.id}
                                                    isFirst={index === 0}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            ) : (
                                <div className="text-center py-16">
                                    <Dumbbell size={64} className="mx-auto mb-4 text-[#bca8d1] dark:text-white/40" />
                                    <h3 className="mb-2 text-lg font-medium text-[#3a3a3a] dark:text-white">
                                        Nessun esercizio selezionato
                                    </h3>
                                    <p className="mb-6 text-[#3a3a3a]/75 dark:text-white/70">
                                        Vai alla tab "Selezione Esercizi" per aggiungere esercizi alla scheda
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('selection')}
                                        className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-white transition-colors hover:bg-emerald-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                                    >
                                        <Search className="h-4 w-4 mr-2" />
                                        Seleziona Esercizi
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-[#dad8e5] bg-[#eeebf5] px-6 py-4 dark:border-white/10 dark:bg-gray-800">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-[#3a3a3a]/80 dark:text-white/80">
                            {selectedExercises.length > 0 && (
                                <span>
                                    {selectedExercises.length} esercizi selezionati • 
                                    {selectedExercises.reduce((total, ex) => total + (ex.serie || 0), 0)} serie totali
                                </span>
                            )}
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-lg border border-[#dad8e5] bg-white px-4 py-2 text-[#3a3a3a] transition-colors hover:bg-[#e3deef] dark:border-white/20 dark:bg-gray-800 dark:text-white dark:hover:brightness-110"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={loading || Object.keys(validationErrors).length > 0}
                                className="flex items-center gap-2 rounded-lg bg-emerald-600 px-6 py-2 text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-400 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110 dark:disabled:opacity-50"
                            >
                                {loading ? <LoadingSpinner size="sm" /> : <Save size={16} />}
                                Salva Modifiche
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal Creazione Esercizio */}
            {showAddExerciseForm && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
                    <div className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[#dad8e5] bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
                        <div className="flex shrink-0 items-center justify-between border-b border-[#dad8e5] bg-[#dad8e5] px-6 py-4 dark:border-white/10 dark:bg-gray-800">
                            <h3 className="text-lg font-semibold text-[#3a3a3a] dark:font-open-sans dark:text-white">
                                Crea Nuovo Esercizio
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowAddExerciseForm(false)}
                                className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-black/5 dark:text-white/90 dark:hover:bg-white/10"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="max-h-[calc(85vh-80px)] overflow-y-auto bg-[#faf9fc] p-6 dark:bg-gray-900">
                            <ImprovedUserExerciseForm
                                embedded
                                onClose={() => setShowAddExerciseForm(false)}
                                onSuccess={handleExerciseCreated}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}